<template>
  <div class="retention-analysis-table-cont">
    <t-table ref="table" row-key="id" :columns="columns" :data="data" :pagination="pagination">
      <template #data-sum-title>
        <t-dropdown
          :options="[
            { content: '求和', value: 1 },
            { content: '平均值', value: 2 },
          ]"
          @click="handleShowDataType"
        >
          <t-button variant="text">
            <div style="display: flex; align-items: center; font-weight: 500">
              <span style="margin: 0 5px 0 0; width: 40px">{{ showDataSum ? '求和' : '平均值' }}</span>
              <icon name="chevron-down-circle" size="12"></icon>
            </div>
          </t-button>
        </t-dropdown>
      </template>
      <template #data-sum-cell="{ row }">
        {{ showDataSum ? row.dataSum : row.dataAvg }}
      </template>
    </t-table>
  </div>
</template>

<script>
import lodash from 'lodash';
import numeral from 'numeral';

export default {
  name: 'DistributeDataTable',
  props: {
    analysisData: {
      type: Object,
    },
    chartType: {
      type: String,
    },
  },
  data() {
    return {
      showDataSum: true,
      columns: [],
      data: [],

      pagination: {
        defaultCurrent: 1,
        defaultPageSize: 10,
        total: 0,
      },
    };
  },
  watch: {
    analysisData: {
      handler() {
        this.init();
      },
      deep: true,
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.columns = this.generatedColumn();
      this.data = this.translateAnalysisData();
      this.pagination.total = this.data.length;
    },
    translateAnalysisData() {
      const { chartType, analysisData } = this;
      if (!analysisData.dataItemList || !analysisData.dataItemList.length) {
        return [];
      }
      const dataArr = [];
      if (chartType === 'trend') {
        analysisData.dataItemList.forEach((dataItem, index) => {
          const row = {
            id: index,
            eventName: `A1 ${analysisData.eventName}`,
            globalFilterName: dataItem.globalFilterName || '全体用户',
            groupByKey: dataItem.groupByKey && dataItem.groupByKey !== 'ALL' ? dataItem.groupByKey : '总体',
            distributionScope: dataItem.distributionScope,
            dataSum: numeral(dataItem.dataSum).format('0,0'),
            dataAvg: numeral(dataItem.dataAvg).format('0,0.00'),
          };

          dataItem.data.forEach((subDataItem, subDataIndex) => {
            row[`item${subDataIndex}`] = numeral(subDataItem).format('0,0');
          });

          dataArr.push(row);
        });
      } else {
        const cacheMap = {};
        analysisData.dataItemList.forEach(dataItem => {
          const key = `${dataItem.globalFilterName}-${dataItem.groupByKey}`;
          let arr = cacheMap[key];
          if (!arr) {
            arr = [];
            cacheMap[key] = arr;
          }
          arr.push(dataItem);
        });

        Object.keys(cacheMap).forEach((key, index) => {
          const arr = cacheMap[key];

          const row = {
            id: index,
            eventName: `A1 ${analysisData.eventName}`,
            groupByKey: arr[0] && arr[0].groupByKey && arr[0].groupByKey !== 'ALL' ? arr[0].groupByKey : '总体',
          };

          let sum = 0;
          analysisData.distributionScopeList.forEach((scope, subIndex) => {
            const dataItem = arr.find(item => item.distributionScope === scope) || { dataSum: 0 };
            row[`item${subIndex}`] = numeral(dataItem.dataSum).format('0,0');
            sum += dataItem.dataSum;
          });

          row.dataSum = numeral(sum).format('0,0');
          dataArr.push(row);
        });
      }

      return dataArr;
    },
    generatedColumn() {
      let currColumns = [{ colKey: 'eventName', title: '事件/日期', fixed: 'left', width: 200 }];
      const { chartType, analysisData } = this;

      const { dateList, distributionScopeList } = analysisData;

      const fieldList = chartType === 'trend' ? dateList : distributionScopeList;

      if (!fieldList || !fieldList.length) {
        return currColumns;
      }
      if (chartType === 'trend') {
        currColumns = currColumns.concat([
          { colKey: 'globalFilterName', title: '全局筛选', fixed: 'left', width: 200 },
          { colKey: 'groupByKey', title: '分组维度', fixed: 'left', width: 200 },
          { colKey: 'distributionScope', title: '区间', fixed: 'left', width: 200 },
          {
            colKey: 'dataSum',
            title: 'data-sum-title',
            cell: 'data-sum-cell',
            fixed: 'left',
            width: 200,
          },
        ]);
      } else {
        currColumns = currColumns.concat([
          { colKey: 'groupByKey', title: '分组维度', fixed: 'left', width: 200 },
          {
            colKey: 'dataSum',
            title: '总数',
            fixed: 'left',
            width: 200,
          },
        ]);
      }

      fieldList.forEach((item, index) => {
        currColumns.push({
          colKey: `item${index}`,
          title: item,
          width: 150,
        });
      });
      return currColumns;
    },
    handleShowDataType({ value }) {
      this.showDataSum = value === 1;
    },
  },
};
</script>

<style scoped lang="less">
.retention-analysis-table-cont {
  width: 100%;
  overflow-y: auto;

  ::v-deep .t-table {
    width: 99%;

    thead td {
      font-weight: 500;
      &.t-table__expandable-icon-cell {
        position: sticky;
        left: 0;
      }
    }
    .t-table__expanded-row td {
      padding: 0;
    }
    .t-table__tree-col svg:first-child {
      display: none;
    }
    tbody td {
      height: 74px;

      &.analysis-data-cell {
        padding: 0;
        border-right: 1px solid #e7e7e7;

        .analysis-data {
          box-sizing: border-box;
          padding: 12px 7px 11px 7px;
          position: relative;

          &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 8px;
          }

          &.cell-bg-1:after {
            background-color: #dddddd;
          }
          &.cell-bg-2:after {
            background-color: #c9e4ff;
          }
          &.cell-bg-3:after {
            background-color: #60a7ff;
          }
          &.cell-bg-4:after {
            background-color: #348bff;
          }
          &.cell-bg-5:after {
            background-color: #0d6ce4;
          }

          p {
            margin: 0;
            width: 100%;
            height: 21px;
            line-height: 21px;
            font-weight: 700;
            font-size: 14px;
            color: #333;
            text-align: right;
            &:first-child {
              margin-bottom: 8px;
            }
          }
        }
      }
    }
  }
}
</style>
